import { useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg';

import { getLocationsForStationMapper } from './utils';

import mapper from '../../images/icons/menu/mapper-action.svg';
import mapValidator from '../../images/icons/menu/map-validator.svg';
import openReport from '../../images/icons/analytics/open-report.svg';
import trashCan from '../../images/icons/menu/trash-can.svg';

const MAP_EDITOR_BASE_URL = 'https://app.mappedin.com/editor/edit/';
const MAP_VALIDATOR_BASE_URL = 'https://mvf-converter.replit.app/';

const getFullAddress = (location) => [
  location?.address1,
  location?.address2,
  location?.city,
  location?.state
].filter(Boolean).join(', ');

const StationMapperPage = ({ userData }) => {
  const [selectedTab, setSelectedTab] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showEditMapModal, setShowEditMapModal] = useState(false);
  const [showMapValidatorModal, setShowMapValidatorModal] = useState(false);
  const [validatorUrl, setValidatorUrl] = useState('');

  useEffect(() => {
      const getLocations = async (customerId) => {
        return await getLocationsForStationMapper(customerId);
      };

      if (userData?.customer_id) {
        getLocations(userData.customer_id)
          .then(data => {
            setLocations(data);
          });
      }
  }, [ userData?.customer_id ]);

  const fillSvgColor = (buttonTitle, svg, stroke, fill) => {
    if (stroke || fill) {
      for (var i = 0; i < svg.children.length; i++) {
        svg.children[i].setAttribute('stroke', stroke);
        if (buttonTitle !== 'Delete') {
          svg.children[i].setAttribute('fill', fill);
        }
      }
    }
  }

  const openModalButtons = [
    {
      key: "selectMap",
      className: "select-map",
      title: "Select a map",
      icon: openReport,
      stroke: null,
      fill: null,
      onClick: () => setShowEditMapModal(true)
    },
    {
      key: "mapValidator",
      className: "map-validator",
      title: "Map validator",
      icon: mapValidator,
      stroke: '#fff',
      fill: '#fff',
      onClick: () => setShowMapValidatorModal(true)
    }
  ];

  const actionButtons = [
    {
      title: "Map editor",
      icon: mapper,
      enabled: true,
      key: "mapEditor",
      tab: "mapEditor"
    },
    {
      key: "endGrouping10",
      separator: true
    },
    {
      title: "Delete",
      icon: trashCan,
      enabled: false,
      key: "delete"
    }
  ];

  const noFillIcons = ["Delete"];

  // The iframe shows the validator whenever one has been requested.
  // Otherwise, an empty iframe.
  const getIframeSrc = () => {
    if (validatorUrl) {
      return validatorUrl;
    }

    return '';
  }

  return (
    <div className="StationMapperPage">
      {
        showEditMapModal &&
          <EditMapModal
            locations={locations}
            setSelectedLocation={setSelectedLocation}
            setShowEditMapModal={setShowEditMapModal}
            setValidatorUrl={setValidatorUrl}
          />
      }
      {
        showMapValidatorModal &&
          <MapValidatorModal
            customerId={userData?.customer_id}
            locations={locations}
            setShowMapValidatorModal={setShowMapValidatorModal}
            setValidatorUrl={setValidatorUrl}
          />
      }
      <div className="station-mapper-actions">
        <div className="buttons-and-name">
          <div className="buttons-container">
            {
              openModalButtons.map(button => (
                <div
                  key={button.key}
                  className={`icon-container open-modal ${button.className}`}
                  onClick={button.onClick}
                  title={button.title}
                >
                  <ReactSVG
                    beforeInjection={(svg) => fillSvgColor(button.title, svg, button.stroke, button.fill)}
                    className="icon feature-icon"
                    src={button.icon}
                  />
                </div>
              ))
            }
          </div>
          <div className="name">
            Actionfigure&reg; Station Mapper
          </div>
        </div>
        <div className="actions-container">
        {
          actionButtons.map(button => {
            if (button.separator) {
              return <div className="vertical-line" key={button.key}></div>
            } else {
              return <div title={button.title} key={button.key}>
                <div className={`icon-container ${!button.enabled ? "disabled" : ""} ${button.tab === selectedTab && selectedLocation?.id ? 'selected-tab' : ''} `}>
                  <ReactSVG
                    beforeInjection={(svg) => !button.enabled || !selectedLocation?.id ?
                      fillSvgColor(button.title, svg, '#CCD2D3', '#CCD2D3') :
                      (
                        button.tab === selectedTab && selectedLocation?.id ?
                        fillSvgColor(button.title, svg, '#fff', '#fff') :
                        fillSvgColor(button.title, svg, '#373739', '#373739')
                      )
                    }
                    className="icon feature-icon"
                    src={button.icon}
                  />
                </div>
              </div>
            }
          })
        }
        </div>
      </div>
      <div id="content">
      <div id="mapper-placeholder">
        {
          !validatorUrl ?
          <div className="mapper-empty-state"></div> :
          <iframe
            height='100%'
            src={getIframeSrc()}
            title="Station Mapper"
            width='100%'
          />
        }
      </div>
      </div>
    </div>
  )
}

const EditMapModal = ({
  locations,
  setSelectedLocation,
  setShowEditMapModal,
  setValidatorUrl
}) => {

  const [highlightLocation, setHighlightLocation] = useState(null);

  const handleCancel = () => {
    setShowEditMapModal(false)
  }

  const handleConfirm = () => {
    setSelectedLocation(highlightLocation);
    // Picking a map takes the iframe back out of validator mode.
    setValidatorUrl('');
    setShowEditMapModal(false);
    const editorUrl = `${MAP_EDITOR_BASE_URL}${highlightLocation.purchasedProducts.mapId}`;
    window.open(editorUrl, '_blank').focus();
  }

  const handleSelect = (location) => {
    setHighlightLocation(location);
  }

  const locationRows = locations?.map(location => {
    const fullAddress = getFullAddress(location);

    return <tr
      key={location?.id}
      onClick={() => handleSelect(location)}
      className={`location-row ${highlightLocation?.id === location?.id ? 'highlight-row' : ''}`}
    >
      <td>{location?.name}</td>
      <td>{fullAddress}</td>
    </tr>
  });

  return <div className="modal-wrapper">
    <div className="modal">
      <div className="modal-header">
        <h2>Edit a map</h2>
      </div>
      <div className="table-container">
        <table>
          <tbody>
            <tr className="header-row">
              <th className="table-header">Name</th>
              <th className="table-header">Address</th>
            </tr>
            {locationRows}
          </tbody>
        </table>
      </div>
      <div className="submit-buttons-container">
        <button className="button cancel-button" onClick={handleCancel}>
          Cancel
        </button>
        <button className="button modal-button" onClick={handleConfirm}>
          Open
        </button>
      </div>
    </div>
  </div>
}

const MapValidatorModal = ({
  customerId,
  locations,
  setShowMapValidatorModal,
  setValidatorUrl
}) => {

  const [highlightLocations, setHighlightLocations] = useState([]);

  const isHighlighted = (location) => (
    highlightLocations.some(highlighted => highlighted?.id === location?.id)
  );

  const handleCancel = () => {
    setShowMapValidatorModal(false);
  }

  const handleSelect = (location) => {
    setHighlightLocations(previous => (
      previous.some(highlighted => highlighted?.id === location?.id)
        ? previous.filter(highlighted => highlighted?.id !== location?.id)
        : [ ...previous, location ]
    ));
  }

  const handleGo = () => {
    const mapIds = highlightLocations
      .map(location => location?.purchasedProducts?.mapId)
      .filter(Boolean)
      .map(encodeURIComponent);

    setValidatorUrl(
      `${MAP_VALIDATOR_BASE_URL}?id=${encodeURIComponent(customerId)}&mapid=${mapIds.join(',')}`
    );
    setShowMapValidatorModal(false);
  }

  const locationRows = locations?.map(location => {
    const fullAddress = getFullAddress(location);

    return <tr
      key={location?.id}
      onClick={() => handleSelect(location)}
      className={`location-row ${isHighlighted(location) ? 'highlight-row' : ''}`}
    >
      <td className="checkbox-cell">
        <input
          type="checkbox"
          checked={isHighlighted(location)}
          onChange={() => handleSelect(location)}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td>{location?.name}</td>
      <td>{fullAddress}</td>
    </tr>
  });

  return <div className="modal-wrapper">
    <div className="modal">
      <div className="modal-header">
        <h2>Validate maps</h2>
      </div>
      <div className="table-container">
        <table>
          <tbody>
            <tr className="header-row">
              <th className="table-header checkbox-header"></th>
              <th className="table-header">Name</th>
              <th className="table-header">Address</th>
            </tr>
            {locationRows}
          </tbody>
        </table>
      </div>
      <div className="submit-buttons-container">
        <button className="button cancel-button" onClick={handleCancel}>
          Cancel
        </button>
        <button
          className="button modal-button"
          disabled={!highlightLocations.length}
          onClick={handleGo}
        >
          Go
        </button>
      </div>
    </div>
  </div>
}

export default StationMapperPage
